<template>
  <div class="full-width">
    <div
      ref="dataViewRow" class="row" :class="{ 'row-selected': props.isSelected }" :style="`grid-template-columns: ${columnsGridTemplate(props.columns)}`"
      @click="() => editDraft(props.item)"
    >
      <div class="head checkbox-cell" @click.stop>
        <input
          type="checkbox"
          class="row-checkbox"
          :checked="props.isSelected"
          @change="handleCheckboxChange"
        >
      </div>
      <div class="head first" :class="shouldHideOnBreakpoint(props.columns, 'video_title')">
        <VideoComponent
          remove-add-button
          is-cover
          :item="adaptedItem"
          :hover-animation="false"
          :with-title-events="false"
          :enable-click-action="false"
          :show-time="false"
          :show-ownership="false"
          :show-top-text="false"
          :show-orientation="false"
          :show-new-label="false"
          class="video-component"
        />
        <div dir="auto" class="video-title">
          <NewTooltip :placement="'top'" :content="props.item.video_title" auto-ellipsis />
        </div>
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'duration')" class="head">
        {{ formatDuration(item.duration) }}
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'orientation')" class="head">
        <VideoLayoutOrientation :is-vertical="item.orientation === Orientation.Vertical" :tag-colors="TagColors.White" :size="TagSizes.Small" show-img />
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'video_settings')" class="head">
        <Label
          :text="item.video_settings ? $t('video-new-upload-settings-set') : $t('video-new-upload-no-settings')"
          :color="item.video_settings ? LabelColors.Green : LabelColors.Yellow"
        />
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'created_at')">{{ getDateString(item.created_at) }}</div>

      <ActionButtons :actions="actions" :is-visible="isRowHovered" :class="shouldHideOnBreakpoint(props.columns, 'actions')" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref } from 'vue'
  import { useElementHover } from '@vueuse/core'
  import { useRouter } from 'vue-router'
  import { useStore } from 'vuex'
  import { useI18n } from 'vue-i18n'
import { getDateString, formatDuration, openModalCustomConfirmation } from '@/services/utils'
import VideoComponent from '@/components/base/VideoComponent.vue'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import { columnsGridTemplate, shouldHideOnBreakpoint } from '@/services/layoutUtils'
import { CRUD, DataActions, DataType, ListColumn, Orientation } from '@/interfaces'
import { apiService } from '@/services'
import { VIDEO_ACTIONS_INJECTION_KEY, BULK_SELECT_INJECTION_KEY } from '@/constants'
import { ModalType } from '@/components/modals/modals-util'
import { VideoLayoutOrientation } from '@/components/common'
import { TagColors, TagSizes } from '@/components/common/tag/utils'
import NewTooltip from '@/components/common/tooltips/NewTooltip.vue'
import Label from '@/components/common/label/Label.vue'
import { LabelColors } from '@/components/common/label/utils'
import { VideoDraft } from '@/services/video-drafts/types'
import type { useVideoDraftsEdit } from '@/composables/useVideoDraftsEdit/useVideoDraftsEdit'
import { VIDEO_DRAFT_EDIT_INJECTION_KEY } from '@/constants'
import { VideoDraftItem, Thumbnail } from '@/services/videos/types'

const props = defineProps<{
  columns: ListColumn[]
  item: VideoDraft
  isSelected?: boolean
}>()
const emit = defineEmits<{
  'toggle-selection': []
}>()
const store = useStore()
const router = useRouter()
const videoDraftEdit= inject<ReturnType<typeof useVideoDraftsEdit>>(VIDEO_DRAFT_EDIT_INJECTION_KEY)!
const { t } = useI18n()
const modalType = ModalType.VIDEOS_DELETE_VIDEO_MODAL
const dataActions = inject<DataActions>(VIDEO_ACTIONS_INJECTION_KEY)
const bulkSelect = inject(BULK_SELECT_INJECTION_KEY)!
const dataViewRow = ref()
const isRowHovered = useElementHover(dataViewRow)

function handleCheckboxChange() {
  emit('toggle-selection')
}

const adaptedItem = computed((): VideoDraftItem => {
  const thumbnails = props.item.payload?.thumbnails || []
  return {
    video_id: props.item.id?.toString() || '',
    is_vertical: props.item.orientation === Orientation.Vertical,
    thumbnails: thumbnails as Thumbnail[],
    title: props.item.video_title,
    duration_in_ms: props.item.duration
  }
})

const actions = computed(() => [
  {
    label: 'edit',
    handler: () => editDraft(props.item),
    translationKey: 'content-video-edit'
  },
  {
    label: 'delete',
    handler: () => openDeleteDraft(),
    translationKey: 'content-video-delete'
  }
])

const editDraft = (item: VideoDraft) => {
  videoDraftEdit.setDraftsToEdit([item])
  router.push({ name: 'add-video' })
}

const deleteDraft = async () => {
  try {
    await apiService.videoDrafts.deleteVideoDrafts([props.item.id])
    if (bulkSelect) {
      bulkSelect.removeItems([props.item.id])
    }
    if (dataActions?.clearSearch) dataActions.clearSearch()
    store.dispatch('messages/addMessage', {
      message: t('video-deleted'),
      type: 'success'
    })
  } catch (err: unknown) {
    console.warn(err)
  }
}

function openDeleteDraft() {
  openModalCustomConfirmation({ action: CRUD.Delete, type: DataType.VideoDraft, id: props.item.id, hideInput: true, onConfirm: deleteDraft }, modalType)
}

</script>

<style scoped lang="scss">
.row {
  width: 100%;
  height: 86px;
  display: grid;
  align-items: center;
  justify-items: start;

  > div {
    padding: 1rem;
    width: 100%;
  }

  &:hover {
    background-color: $rowSelectedBackground;
    cursor: pointer;
  }

  &.row-selected {
    background-color: $rowSelectedBackground;
  }

  .head {
    display: flex;
    align-items: center;
    width: 100%;
    overflow: hidden;

    &.checkbox-cell {
      cursor: default;
      justify-content: center;
      width: 100%;
      height: 100%;
    }

    &.first {
      display: flex;
      gap: 1rem;
    }

    .video-component {
      min-width: 4rem;
      min-height: 4rem;
      max-width: 4rem;
      max-height: 4rem;
    }

    .video-title {
      overflow: hidden;
      display: grid;
      grid-template-columns: 100%;
    }
  }

  .icon {
    cursor: pointer;
  }

}
</style>

